"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"

import { SparkleIcon, ChecklistIcon } from "@/components/ui/icons"
import { BiomarkerListModal } from "@/components/biomarkers/biomarker-list-modal"
import { track } from "@/lib/analytics"
import { buildBiomarkerSeedMessage } from "@/lib/biomarkers/list-seed"
import { cn } from "@/lib/utils"

/**
 * Biomarker AI entry points — one compact chip row (the `ai-biomarker-embed-v1`
 * test arm). A handful of contextual starter-prompt chips, then a trailing
 * dark-blue "check your list" chip that opens the paste modal. Everything seeds
 * the hosted Ella embed. Deliberately a single flex-wrap row (no promo block) so
 * it stays above the fold and doesn't push the explorer down.
 */

const PAGE_PROMPTS = [
  { id: "heart", promptKey: "biomarker.page.heart" },
  { id: "tired", promptKey: "biomarker.page.tired" },
  { id: "cholesterol", promptKey: "biomarker.page.cholesterol" },
  { id: "included", promptKey: "biomarker.page.included" },
] as const

const CHIP =
  "type-body-sm inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 transition-colors"

/** Count the terms a pasted list holds, for the analytics payload only. */
function countTerms(rawList: string): number {
  return rawList
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean).length
}

export interface BiomarkerAiEntrypointsProps {
  /** Opens the embed launcher pre-seeded with the chosen prompt / pasted list. */
  onSeed: (prompt: string, promptKey: string) => void
  className?: string
}

export function BiomarkerAiEntrypoints({
  onSeed,
  className,
}: BiomarkerAiEntrypointsProps) {
  const t = useTranslations("chat.starterPrompts")
  const tc = useTranslations("biomarkers.listCheck")
  const [open, setOpen] = useState(false)

  return (
    <>
      {/* One row, never wraps: fits on desktop, scrolls as a bar on mobile.
          Bleeds edge-to-edge on mobile (-mx-4 px-4) so the overflow reads as
          scrollable rather than cut off; scrollbar hidden. */}
      <div
        data-testid="biomarker-ai-prompts"
        className={cn(
          "-mx-4 flex items-center gap-2 overflow-x-auto px-4 md:mx-0 md:px-0",
          "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className
        )}
      >
        {PAGE_PROMPTS.map(({ id, promptKey }) => (
          <button
            key={promptKey}
            type="button"
            onClick={() => {
              track.biomarkerStarterPromptClicked(promptKey)
              onSeed(t(`${id}.prompt`), promptKey)
            }}
            className={cn(
              CHIP,
              "bg-neutral-100 text-neutral-700 hover:bg-neutral-200 hover:text-neutral-900"
            )}
          >
            <SparkleIcon
              width={14}
              height={14}
              className="text-aubergine-600"
            />
            {t(`${id}.label`)}
          </button>
        ))}

        {/* The look-up tool, folded into the row as a dark-blue chip (DS "CTA =
            navy content") rather than a full-width promo block. */}
        <button
          type="button"
          data-testid="biomarker-check-cta"
          onClick={() => setOpen(true)}
          className={cn(
            CHIP,
            "bg-deep-blue-950 hover:bg-deep-blue-900 text-white"
          )}
        >
          <ChecklistIcon size={14} />
          {tc("button")}
        </button>
      </div>

      <BiomarkerListModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={(rawList) => {
          setOpen(false)
          track.biomarkerListSubmitted(countTerms(rawList))
          onSeed(buildBiomarkerSeedMessage(rawList), "biomarker.list-check")
        }}
      />
    </>
  )
}
